import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { BidiModule } from '@angular/cdk/bidi';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PwaUpdateService } from 'app/core/pwa-update/pwa-update.service';
import { AuthService } from 'app/core/auth/auth.service';
import { isRequesterPortalContext } from 'app/core/auth/requester-user';
import { CallsWidgetComponent } from './layout/common/calls-widget/calls-widget.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { Inject, Renderer2 } from '@angular/core';
import { LanguageService, LayoutDirection, SupportedLanguage } from 'app/services/language.service';
import { CompanyService } from 'app/services/company.service';
import { PermissionService } from 'app/core/services/permission.service';
import { ApiService } from 'app/shared/apis/api.service';
import { UserModel } from 'app/models/user.model';
import { AccessApprovalService } from 'app/services/access-approval.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, CommonModule, CallsWidgetComponent, BidiModule],
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private driverViewActive = false;
    private shareViewActive = false;
    private budgetPublicActive = false;
    private requesterPortalActive = false;
    private user: UserModel | null = null;
    private currentUrl = '';
    direction: LayoutDirection = 'ltr';

    /**
     * Rotas alcançáveis mesmo enquanto a entrada não é ALLOWED — a própria
     * tela de espera, fluxos de autenticação e o viewer público de
     * acompanhamento. Tudo mais (inclusive rotas do app do motorista) é
     * redirecionado de volta pra `/access-approval/status`.
     */
    private static readonly ACCESS_APPROVAL_EXEMPT_PREFIXES = [
        '/access-approval',
        '/sign-in',
        '/sign-out',
        '/sign-up',
        '/sign-up-type',
        '/cadastro-medico',
        '/sign-up-medic-success',
        '/forgot-password',
        '/reset-password',
        '/confirmation-required',
        '/change-password',
        '/not-found-company',
        '/acompanhar',
        '/teleconsulta',
    ];

    constructor(
        private readonly pwaUpdate: PwaUpdateService,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly languageService: LanguageService,
        private readonly companyService: CompanyService,
        private readonly permissionService: PermissionService,
        private readonly accessApprovalService: AccessApprovalService,
        private readonly renderer: Renderer2,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    ngOnInit(): void {
        this.languageService
            .getLanguage$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((language) => this.applyDocumentDirection(language));

        ApiService.userCurrent.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            this.user = user;
            this.syncCallsWidgetVisibility(this.router.url);
        });

        this.companyService.currentCompany$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.syncCallsWidgetVisibility(this.router.url);
        });

        this.currentUrl = this.router.url;
        this.syncCallsWidgetVisibility(this.currentUrl);
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe((event) => {
                const navigation = event as NavigationEnd;
                this.currentUrl = navigation.urlAfterRedirects;
                this.syncCallsWidgetVisibility(this.currentUrl);
                this.enforceAccessApproval();
            });

        // Também reage a mudanças de estado sem navegação nova (ex.: o
        // médico regulador aprova/rejeita via push de socket enquanto a
        // pessoa já está sentada numa tela).
        this.accessApprovalService.state$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.enforceAccessApproval();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get isAuthenticated(): boolean {
        return this.authService.isAuthenticated();
    }

    get showCallsWidget(): boolean {
        return (
            this.isAuthenticated &&
            !this.driverViewActive &&
            !this.shareViewActive &&
            !this.budgetPublicActive &&
            !this.requesterPortalActive &&
            this.permissionService.hasPermission('CONVERSATION_VIEW')
        );
    }

    /**
     * Bloqueio global: enquanto a entrada não é ALLOWED, qualquer rota fora
     * da lista de exceções redireciona pra `/access-approval/status` — não
     * só a rota `home` (o guard lá cobre a entrada normal pós-login; isto
     * aqui cobre navegação direta/deep link pra qualquer outra tela).
     */
    private enforceAccessApproval(): void {
        const state = this.accessApprovalService.state$.value;
        if (!state || state.status === 'ALLOWED') return;
        if (this.isAccessApprovalExemptRoute(this.currentUrl)) return;
        this.router.navigate(['/access-approval/status']);
    }

    private isAccessApprovalExemptRoute(url: string): boolean {
        const path = (url || '').split('?')[0];
        return AppComponent.ACCESS_APPROVAL_EXEMPT_PREFIXES.some(
            (prefix) => path === prefix || path.startsWith(`${prefix}/`),
        );
    }

    private syncCallsWidgetVisibility(url: string): void {
        const path = (url || '').split('?')[0];
        this.driverViewActive = path === '/driver' || path.startsWith('/driver/');
        this.shareViewActive =
            path === '/acompanhar' || path.startsWith('/acompanhar/') || path.startsWith('/teleconsulta/');
        this.budgetPublicActive = path === '/budget' || path.startsWith('/budget/');
        this.requesterPortalActive = isRequesterPortalContext(this.user, this.companyService.getCurrentCompany(), path);
    }

    private applyDocumentDirection(language: SupportedLanguage): void {
        this.direction = this.languageService.getDirection(language);
        this.renderer.setAttribute(this.document.documentElement, 'lang', this.languageService.getDocumentLanguageCode(language));
        this.renderer.setAttribute(this.document.documentElement, 'dir', this.direction);
        this.renderer.setAttribute(this.document.body, 'dir', this.direction);
    }
}
